import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  createDeferredEndDragHandlers,
  createDeferredEndDragTimeoutScheduler,
} from '~/components/Shared/Drag/createDeferredEndDragHandlers';

type Html5BackendFactory = typeof HTML5Backend;

type Html5BackendTopHandlers = {
  handleTopDragEndCapture: (event: Event) => void;
  handleTopDragStart: (event: Event) => void;
  handleTopDrop: (event: Event) => void;
  teardown: () => void;
};

type CreateHtml5BackendWithDeferredEndDragOptions = {
  isFixEnabled: () => boolean;
};

// Cover the next macrotask / rAF / setTimeout(0) replay. Longer holds still skip hover
// instead of throwing; stretching this only keeps the ghost on cancelled drags.
const DEFERRED_END_DRAG_MS = 100;

const isHtml5BackendTopHandlers = (backend: object): backend is Html5BackendTopHandlers => {
  if (
    !('handleTopDragEndCapture' in backend) ||
    !('handleTopDragStart' in backend) ||
    !('handleTopDrop' in backend) ||
    !('teardown' in backend)
  ) {
    return false;
  }

  const { handleTopDragEndCapture, handleTopDragStart, handleTopDrop, teardown } = backend;

  return (
    typeof handleTopDragEndCapture === 'function' &&
    typeof handleTopDragStart === 'function' &&
    typeof handleTopDrop === 'function' &&
    typeof teardown === 'function'
  );
};

export const createHtml5BackendWithDeferredEndDrag =
  ({ isFixEnabled }: CreateHtml5BackendWithDeferredEndDragOptions): Html5BackendFactory =>
  (manager, context, options) => {
    const backend = HTML5Backend(manager, context, options);

    if (!isHtml5BackendTopHandlers(backend)) {
      return backend;
    }

    const deferredHandlers = createDeferredEndDragHandlers({
      handleTopDragEndCapture: backend.handleTopDragEndCapture,
      handleTopDragStart: backend.handleTopDragStart,
      handleTopDrop: backend.handleTopDrop,
      isDragging: () => manager.getMonitor().isDragging(),
      isFixEnabled,
      scheduler: createDeferredEndDragTimeoutScheduler(DEFERRED_END_DRAG_MS),
      teardown: backend.teardown.bind(backend),
    });

    backend.handleTopDragEndCapture = deferredHandlers.handleTopDragEndCapture;
    backend.handleTopDragStart = deferredHandlers.handleTopDragStart;
    backend.handleTopDrop = deferredHandlers.handleTopDrop;
    backend.teardown = deferredHandlers.teardown;

    return backend;
  };
